import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { locationMaster } from '../utils/locationMaster';
import {
  FaBuilding,
  FaLocationArrow,
  FaIdCard,
  FaGlobeAsia,
  FaAt,
  FaBarcode,
  FaUniversity,
} from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL;

const initialVendorState = {
  company_id: '',
  company_name: '',
  address: '',
  pancard: '',
  city: '',
  state: '',
  country: '',
  gst_number: '',
  email: '',
  opening_balance: '',
};

type VendorType = typeof initialVendorState & {
  id?: string | number;
};

type CompanyOption = {
  id: string | number;
  company_name: string;
};

const inputStyle =
  "flex items-center bg-white rounded-lg border border-gray-300 px-3 py-2 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition-colors shadow-sm w-full";
const labelStyle = "block mb-1 text-sm font-medium text-blue-900 tracking-wide";
const inputInnerStyle = "flex-1 outline-none bg-transparent w-full placeholder:truncate";

const AddVendor: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const vendorId = searchParams.get('vendor_id');
  const isEditMode = vendorId && vendorId !== 'new';

  const [vendor, setVendor] = useState<VendorType>(initialVendorState);
  const [companyList, setCompanyList] = useState<CompanyOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  useEffect(() => {
    async function fetchCompanies() {
      try {
        const compRes = await axios.get(`${API_URL}/auth/companies`);
        setCompanyList(Array.isArray(compRes.data) ? compRes.data : []);
        
        if (!isEditMode) {
          const activeWorkspaceId = localStorage.getItem("selected_company_id");
          if (activeWorkspaceId && activeWorkspaceId !== "all") {
            setVendor((prev) => ({
              ...prev,
              company_id: activeWorkspaceId,
            }));
          }
        }
      } catch (err) {
        console.error("Failed to load companies list", err);
      }
    }
    fetchCompanies();
  }, [isEditMode]);

  useEffect(() => {
    if (isEditMode) {
      setLoading(true);
      setError('');
      axios
        .get(`${API_URL}/auth/vendor/${vendorId}`)
        .then((res) => {
          if (res.data) {
            setVendor({
              company_id: res.data.company_id || '',
              company_name: res.data.company_name || '',
              address: res.data.address || '',
              pancard: res.data.pancard || '',
              city: res.data.city || '',
              state: res.data.state || '',
              country: res.data.country || '',
              gst_number: res.data.gst_number || '',
              email: res.data.email || '',
              opening_balance: res.data.opening_balance !== undefined ? String(res.data.opening_balance) : '',
            });
          }
        })
        .catch((err) => {
          setError('Failed to load vendor details.');
          toast.error('Failed to load vendor details.');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setVendor(initialVendorState);
    }
  }, [vendorId, isEditMode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setVendor((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "country") {
        updated.state = "";
        updated.city = "";
      } else if (name === "state") {
        updated.city = "";
      }
      return updated;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vendor.company_name) {
      toast.error('Vendor Name is required.');
      return;
    }
    setSubmitting(true);
    setError('');
    setSuccess('');

    const payload = {
      ...vendor,
      company_id: vendor.company_id ? Number(vendor.company_id) : null,
      opening_balance: vendor.opening_balance ? Number(vendor.opening_balance) : 0,
    };

    try {
      if (isEditMode) {
        await axios.put(`${API_URL}/auth/vendor/${vendorId}`, payload);
        toast.success('Vendor updated successfully!');
        setSuccess('Vendor updated successfully!');
      } else {
        await axios.post(`${API_URL}/auth/vendor`, payload);
        toast.success('Vendor added successfully!');
        setSuccess('Vendor added successfully!');
      }
      setTimeout(() => navigate('/vendors'), 1000);
    } catch (err: any) {
      const apiErr = err.response?.data?.error || err.response?.data?.message || 'Failed to save vendor details.';
      setError(apiErr);
      toast.error(apiErr);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-gray-500 font-semibold">Loading Vendor Form...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-extrabold text-blue-900">
          {isEditMode ? '✏️ Edit Vendor' : '➕ Add New Vendor'}
        </h2>
        <button
          onClick={() => navigate('/vendors')}
          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm font-semibold"
        >
          Cancel & Back
        </button>
      </div>

      {error && <div className="bg-red-50 text-red-700 border border-red-150 p-4 rounded-xl mb-4 font-semibold text-sm">{error}</div>}
      {success && <div className="bg-green-50 text-green-700 border border-green-150 p-4 rounded-xl mb-4 font-semibold text-sm">{success}</div>}

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-blue-50 shadow-xl p-8 space-y-6">
        
        {/* Vendor General Section */}
        <div>
          <h3 className="text-base font-bold text-blue-950 mb-4 flex items-center gap-2 border-b pb-2">
            <FaBuilding className="text-blue-600" /> Basic Details
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className={labelStyle}>Vendor Name / Company Name *</label>
              <div className={inputStyle}>
                <input
                  type="text"
                  name="company_name"
                  value={vendor.company_name}
                  onChange={handleChange}
                  placeholder="Enter vendor business name"
                  className={inputInnerStyle}
                  required
                />
              </div>
            </div>

            <div>
              <label className={labelStyle}>Map to Client Company</label>
              <div className={inputStyle}>
                <select
                  name="company_id"
                  value={vendor.company_id}
                  onChange={handleChange}
                  className={inputInnerStyle}
                >
                  <option value="">-- Choose Company --</option>
                  {companyList.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.company_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Taxes & Balances Section */}
        <div>
          <h3 className="text-base font-bold text-blue-950 mb-4 flex items-center gap-2 border-b pb-2">
            <FaIdCard className="text-blue-600" /> Taxation & Balances
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <label className={labelStyle}>PAN Card Number</label>
              <div className={inputStyle}>
                <input
                  type="text"
                  name="pancard"
                  value={vendor.pancard}
                  onChange={handleChange}
                  placeholder="ABCDE1234F"
                  className={inputInnerStyle}
                />
              </div>
            </div>

            <div>
              <label className={labelStyle}>GST Number</label>
              <div className={inputStyle}>
                <input
                  type="text"
                  name="gst_number"
                  value={vendor.gst_number}
                  onChange={handleChange}
                  placeholder="22AAAAA1111A1Z1"
                  className={inputInnerStyle}
                />
              </div>
            </div>

            <div>
              <label className={labelStyle}>Opening Balance (₹)</label>
              <div className={inputStyle}>
                <input
                  type="number"
                  name="opening_balance"
                  value={vendor.opening_balance}
                  onChange={handleChange}
                  placeholder="0.00"
                  className={inputInnerStyle}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information Section */}
        <div>
          <h3 className="text-base font-bold text-blue-950 mb-4 flex items-center gap-2 border-b pb-2">
            <FaAt className="text-blue-600" /> Contact & Communication
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className={labelStyle}>Email Address</label>
              <div className={inputStyle}>
                <input
                  type="email"
                  name="email"
                  value={vendor.email}
                  onChange={handleChange}
                  placeholder="vendor@company.com"
                  className={inputInnerStyle}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Address Section */}
        <div>
          <h3 className="text-base font-bold text-blue-950 mb-4 flex items-center gap-2 border-b pb-2">
            <FaGlobeAsia className="text-blue-600" /> Billing Address
          </h3>
          <div className="space-y-4">
            <div>
              <label className={labelStyle}>Street Address</label>
              <div className="flex items-center bg-white rounded-lg border border-gray-300 px-3 py-2 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition-colors shadow-sm w-full">
                <textarea
                  name="address"
                  value={vendor.address}
                  onChange={handleChange}
                  placeholder="Enter business registered address"
                  className="flex-1 outline-none bg-transparent w-full h-16 resize-none"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <label className={labelStyle}>Country</label>
                <div className={inputStyle}>
                  <select
                    name="country"
                    value={vendor.country}
                    onChange={handleChange}
                    className={inputInnerStyle}
                  >
                    <option value="">-- Choose Country --</option>
                    {Object.keys(locationMaster).map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className={labelStyle}>State</label>
                <div className={inputStyle}>
                  <select
                    name="state"
                    value={vendor.state}
                    onChange={handleChange}
                    className={inputInnerStyle}
                    disabled={!vendor.country}
                  >
                    <option value="">-- Choose State --</option>
                    {vendor.country &&
                      Object.keys(locationMaster[vendor.country] || {}).map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div>
                <label className={labelStyle}>City</label>
                <div className={inputStyle}>
                  <select
                    name="city"
                    value={vendor.city}
                    onChange={handleChange}
                    className={inputInnerStyle}
                    disabled={!vendor.state}
                  >
                    <option value="">-- Choose City --</option>
                    {vendor.country &&
                      vendor.state &&
                      (locationMaster[vendor.country]?.[vendor.state] || []).map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="flex justify-end pt-4 border-t">
          <button
            type="submit"
            disabled={submitting}
            className={`px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition shadow-md ${
              submitting ? 'opacity-65 cursor-not-allowed' : ''
            }`}
          >
            {submitting ? 'Saving Vendor...' : isEditMode ? 'Update Vendor' : 'Add Vendor'}
          </button>
        </div>

      </form>
    </div>
  );
};

export default AddVendor;
